import type { SelectOption } from '@/components/form/fields'
import type {
  AddressType,
  AttestationPreference,
  ConsentType,
  CustomerStatus,
  Gender,
  KycStatus,
  MaritalStatus,
} from '@/api/types'

/**
 * The enumerations the customer screens offer, spelled exactly as the API serialises them. The
 * label is what an operator reads; the value is what crosses the wire, and the two are kept
 * separate so the wording can be improved without changing what is sent.
 */

export const GENDERS = ['Male', 'Female'] as const satisfies readonly Gender[]

export const MARITAL_STATUSES = [
  'Single',
  'Married',
  'Divorced',
  'Widowed',
] as const satisfies readonly MaritalStatus[]

export const CUSTOMER_STATUSES = [
  'Active',
  'Dormant',
  'Blacklisted',
  'Deceased',
  'Closed',
] as const satisfies readonly CustomerStatus[]

export const KYC_STATUSES = [
  'Draft',
  'PendingReview',
  'Approved',
  'Rejected',
] as const satisfies readonly KycStatus[]

export const GENDER_OPTIONS: SelectOption[] = GENDERS.map((value) => ({ value, label: value }))

export const MARITAL_STATUS_OPTIONS: SelectOption[] = MARITAL_STATUSES.map((value) => ({
  value,
  label: value,
}))

const STATUS_LABELS: Record<CustomerStatus, string> = {
  Active: 'Active',
  Dormant: 'Dormant',
  Blacklisted: 'Blacklisted',
  Deceased: 'Deceased',
  Closed: 'Closed',
}

const KYC_LABELS: Record<KycStatus, string> = {
  Draft: 'Draft',
  PendingReview: 'Pending review',
  Approved: 'Approved',
  Rejected: 'Rejected',
}

export const customerStatusLabel = (status: CustomerStatus) => STATUS_LABELS[status]

export const kycStatusLabel = (status: KycStatus) => KYC_LABELS[status]

export const ADDRESS_TYPE_LABELS: Record<AddressType, string> = {
  Residence: 'Residence',
  Business: 'Business',
  Postal: 'Postal',
}

/** The sixteen administrative regions. A customer address must name one of them. */
export const REGIONS = [
  'Ahafo',
  'Ashanti',
  'Bono',
  'Bono East',
  'Central',
  'Eastern',
  'Greater Accra',
  'North East',
  'Northern',
  'Oti',
  'Savannah',
  'Upper East',
  'Upper West',
  'Volta',
  'Western',
  'Western North',
] as const

export const REGION_OPTIONS: SelectOption[] = REGIONS.map((value) => ({ value, label: value }))

export const NEXT_OF_KIN_RELATIONSHIPS = [
  'Spouse',
  'Parent',
  'Child',
  'Sibling',
  'Other relative',
  'Guardian',
  'Friend',
] as const

export const RELATIONSHIP_OPTIONS: SelectOption[] = NEXT_OF_KIN_RELATIONSHIPS.map((value) => ({
  value,
  label: value,
}))

/**
 * Identifies the wording of the consent statements below. It is stored with every consent so that
 * what a customer agreed to can be reconstructed years later — which is the whole point of
 * recording a consent at all. Changing the wording means a new version, never an edit in place.
 */
export const CONSENT_TEXT_VERSION = '2026-01'

export interface ConsentStatement {
  consentType: ConsentType
  title: string
  /** Shown in full at capture. An operator reads this aloud; it is not a summary of a longer text. */
  text: string
  /** Recorded rather than offered — the basis on which the record is held at all. */
  mandatory: boolean
}

export const CONSENT_STATEMENTS: ConsentStatement[] = [
  {
    consentType: 'AccountOperation',
    title: 'Account operation',
    text: 'I agree that my personal details may be held and processed for the purpose of opening and operating my accounts, and for the institution to meet its legal and regulatory obligations.',
    mandatory: true,
  },
  {
    consentType: 'CreditBureau',
    title: 'Credit bureau',
    text: 'I agree that my credit information may be shared with, and enquired of, licensed credit bureaux.',
    mandatory: false,
  },
  {
    consentType: 'Marketing',
    title: 'Marketing messages',
    text: 'I agree to receive information about products and offers by SMS or email. Transactional alerts about my own accounts are not affected, and I may withdraw this at any time.',
    mandatory: false,
  },
  {
    consentType: 'BiometricCapture',
    title: 'Biometric capture',
    text: "I agree that a fingerprint template may be captured and stored for identity verification at this institution's counters, and I may withdraw this at any time.",
    mandatory: false,
  },
]

/** ISO 3166-1 alpha-2 for Ghana, the default nationality on a new record. */
export const DEFAULT_NATIONALITY = 'GH'

/**
 * How this customer attests to a transaction at the counter, in words a teller reads at the
 * verification dialog and in the media tab. `NotCaptured` is worded as a gap to close, not a
 * fourth attestation method — see the type's own note in `api/types.ts`.
 */
export const ATTESTATION_PREFERENCE_LABELS: Record<AttestationPreference, string> = {
  NotCaptured: 'Not yet recorded',
  Signature: 'Signature',
  Thumbprint: 'Thumbprint',
  Both: 'Signature or thumbprint',
}
